import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Paper,
  SxProps,
  useTheme,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import * as React from "react";

import { RobotoTypography } from "../RobotoTypography";

import { validateDateRange } from "./dateValidation";

export interface QuickFilterOption {
  amount: number | null; // null indicates "All Time" - no time constraints
  unit: dayjs.ManipulateType;
  label: string;
}

export interface DateRange {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
}

interface RobotoDatePickerProps {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  onDateChange: (range: DateRange) => void;
  onApply?: () => void;
  quickFilters?: QuickFilterOption[];
  showApplyButton?: boolean;
  isLoading?: boolean;
  sx?: SxProps;
  startLabel?: string;
  endLabel?: string;
  applyButtonText?: string;
}

// Default quick filter options matching PerActionStats
const DEFAULT_QUICK_FILTERS: QuickFilterOption[] = [
  { amount: 1, unit: "hour", label: "Last Hour" },
  { amount: 24, unit: "hour", label: "Last 24h" },
  { amount: 7, unit: "day", label: "Week" },
  { amount: 1, unit: "month", label: "Month" },
  { amount: null, unit: "day", label: "All Time" }, // unit is ignored when amount is null
];

/**
 * @deprecated Use `DateRangePicker` or `DateTimeRangePicker` from
 * `@/shared/foundation/primitives` instead.
 *
 * Those take and emit ISO strings rather than `Dayjs`, report an unusable
 * window as `null` so a caller never validates one itself, and leave presets
 * and the Apply button to the surface that renders them. Existing callers here
 * keep working; migrate when convenient.
 */
export const RobotoDatePicker: React.FC<RobotoDatePickerProps> = ({
  startDate,
  endDate,
  onDateChange,
  onApply,
  quickFilters = DEFAULT_QUICK_FILTERS,
  showApplyButton = true,
  isLoading = false,
  sx,
  startLabel = "Start Date & Time",
  endLabel = "End Date & Time",
  applyButtonText = "Apply",
}) => {
  const theme = useTheme();
  const currentTimezone = dayjs.tz.guess();

  const setQuickTimeRange = React.useCallback(
    (amount: number | null, unit: dayjs.ManipulateType) => {
      if (amount === null) {
        // "All Time" - no time constraints
        onDateChange({
          startDate: null,
          endDate: null,
        });
      } else {
        const now = dayjs().tz(currentTimezone);
        const startTime = now.subtract(amount, unit);
        onDateChange({
          startDate: startTime,
          endDate: now,
        });
      }
    },
    [currentTimezone, onDateChange],
  );

  const handleStartDateChange = React.useCallback(
    (newValue: Dayjs | null) => {
      onDateChange({
        startDate: newValue,
        endDate,
      });
    },
    [endDate, onDateChange],
  );

  const handleEndDateChange = React.useCallback(
    (newValue: Dayjs | null) => {
      onDateChange({
        startDate,
        endDate: newValue,
      });
    },
    [startDate, onDateChange],
  );

  const validationError = validateDateRange(startDate, endDate);
  const displayError = validationError;

  return (
    <Box sx={sx}>
      <Paper
        variant="outlined"
        sx={{
          padding: theme.spacing(2),
          marginBottom: theme.spacing(2),
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: theme.spacing(2),
            alignItems: "center",
          }}
        >
          <RobotoTypography variant="body2">Select range:</RobotoTypography>

          <DateTimePicker
            label={startLabel}
            value={startDate}
            onChange={handleStartDateChange}
            slotProps={{ textField: { size: "small" } }}
          />

          <DateTimePicker
            label={endLabel}
            value={endDate}
            onChange={handleEndDateChange}
            slotProps={{ textField: { size: "small" } }}
          />

          {quickFilters.length > 0 && (
            <ButtonGroup size="small" variant="outlined">
              {quickFilters.map((filter) => (
                <Button
                  key={`${filter.amount}-${filter.unit}-${filter.label}`}
                  onClick={() => setQuickTimeRange(filter.amount, filter.unit)}
                >
                  {filter.label}
                </Button>
              ))}
            </ButtonGroup>
          )}

          {showApplyButton && onApply && (
            <Button
              variant="contained"
              onClick={onApply}
              disabled={isLoading || !!validationError}
              startIcon={
                isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {applyButtonText}
            </Button>
          )}
        </Box>
      </Paper>

      {displayError && (
        <Paper
          variant="outlined"
          sx={{
            padding: theme.spacing(2),
            marginBottom: theme.spacing(2),
            backgroundColor: theme.palette.error.light,
            color: theme.palette.getContrastText(theme.palette.error.light),
          }}
        >
          <RobotoTypography
            variant="body2"
            component="pre"
            sx={{
              whiteSpace: "pre-wrap",
            }}
          >
            {displayError}
          </RobotoTypography>
        </Paper>
      )}
    </Box>
  );
};
